import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { WhatsAppIcon } from "../components/common/WhatsAppIcon";
import { Reveal } from "../components/motion/Reveal";
import { AnimatedText } from "../components/motion/AnimatedText";
import { FinalCta } from "../components/common/FinalCta";
import { IMAGES } from "../data/site";
import { useSiteData, useC, useWa, fileUrl } from "../context/SiteData";

const OdaDetay = () => {
  const { slug } = useParams();
  const { images, loaded } = useSiteData();
  const c = useC();
  const room = c.rooms.find((r) => r.slug === slug);
  const wa = useWa(room ? `Merhaba, ${room.name} hakkında bilgi almak istiyorum.` : undefined);
  if (!room) return <Navigate to="/odalar" replace />;

  const imgFor = (r) =>
    images[`room-${r.slug}`]
      ? fileUrl(images[`room-${r.slug}`])
      : loaded
        ? r.image || IMAGES.roomExtra
        : null;
  const others = c.rooms.filter((r) => r.slug !== slug);

  return (
    <>
      <section data-testid="room-detail" className="pt-24 sm:pt-28">
        <div className="container-x">
          <Reveal>
            <Link to="/odalar" data-testid="room-back-link" className="link-arrow mt-6 text-ink-500">
              <ArrowLeft size={15} />
              Tüm Odalar
            </Link>
          </Reveal>
          <div className="mt-8 grid gap-10 pb-16 sm:pb-20 lg:grid-cols-2 lg:gap-16 lg:pb-24">
            <Reveal>
              <div className="group overflow-hidden">
                {imgFor(room) ? (
                  <img
                    src={imgFor(room)}
                    alt={room.name}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                    data-testid="room-detail-image"
                  />
                ) : (
                  <div className="aspect-[4/3] w-full animate-pulse bg-cream-200" data-testid="room-detail-image" />
                )}
              </div>
            </Reveal>
            <div>
              <Reveal delay={0.05}>
                <p className="label flex items-center gap-3 text-ink-500">
                  <span className="inline-block h-px w-8 bg-ink-400" />
                  {room.capacity}
                </p>
              </Reveal>
              <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-ink-900 sm:text-4xl lg:text-5xl" data-testid="room-detail-title">
                <AnimatedText text={room.name} delay={0.1} stagger={0.05} immediate />
              </h1>
              <Reveal delay={0.18}>
                <p className="mt-6 text-base leading-relaxed text-ink-500 sm:text-lg">
                  {room.detail}
                </p>
              </Reveal>
              <Reveal delay={0.24}>
                <ul className="mt-8 grid gap-x-6 gap-y-3 border-t border-line pt-8 sm:grid-cols-2" data-testid="room-detail-features">
                  {room.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm font-medium text-ink-800">
                      <Check size={15} className="shrink-0 text-ink-400" />
                      {f}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.3}>
                <a
                  href={wa}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="room-detail-whatsapp"
                  className="btn-primary mt-9"
                >
                  <WhatsAppIcon size={15} />
                  Bu Oda Hakkında Bilgi Al
                </a>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="room-others" className="border-t border-line bg-cream-200">
        <div className="container-x section-y">
          <Reveal>
            <h2 className="text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">
              Diğer oda seçeneği
            </h2>
          </Reveal>
          <div className="mt-10 grid max-w-3xl gap-6">
            {others.map((r, i) => (
              <Reveal key={r.slug} delay={i * 0.1}>
                <Link
                  to={`/odalar/${r.slug}`}
                  data-testid={`room-other-${r.slug}`}
                  className="group relative block overflow-hidden"
                >
                  {imgFor(r) ? (
                    <img
                      src={imgFor(r)}
                      alt={r.name}
                      loading="lazy"
                      className="aspect-[16/9] w-full object-cover saturate-[0.92] transition-[transform,filter] duration-700 group-hover:scale-[1.03] group-hover:saturate-100"
                    />
                  ) : (
                    <div className="aspect-[16/9] w-full animate-pulse bg-cream-200" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-950/75 to-transparent" />
                  <div className="absolute bottom-0 left-0 flex w-full items-center justify-between p-6">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cream-50/70">{r.capacity}</p>
                      <p className="mt-1 text-xl font-bold tracking-tight text-cream-50">{r.name}</p>
                    </div>
                    <ArrowRight size={20} className="text-cream-50 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <FinalCta />
    </>
  );
};

export default OdaDetay;
